import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import UserDashboard from "./pages/UserDashboard/UserDashboard";
import Error404Page from "./pages/PageNotFound/PageNotFound";
import ProtectedRoute from "./components/ProtectedRoutes/ProtectedRoutes";

import Navbar from "./components/Navbar/Navbar";
import GoogleLoginComponent from "./pages/LoginPage/userLogin";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect, useState } from "react";
import constants from "./utils/constants";
import {
  AuthProvider,
  setExternalTrigger,
  useAuthContext,
} from "./context/AuthContext";
import SessionExpiredModal from "./components/SessionExpiredModal/SessionExpiredModal";
import FileUpload from "./pages/AdminDashboard/CaseStudies/Documents";
import ProjectDocumentDetails from "./pages/AdminDashboard/CaseStudies/ProjectDocument";
import PPT from "./pages/AIBot/PPT";
import IndustryTrackUpload from "./pages/AdminDashboard/IndustryTrack/IndustryTrack";
import IndustryTrackDetails from "./pages/AdminDashboard/IndustryTrack/IndustryTrackInfo";
import CertificationsList from "./pages/AdminDashboard/Certifications/CertificationsList";
import VideoDetails from "./pages/AdminDashboard/Videos/VideoDetails";
import Reports from "./pages/AdminDashboard/Reports/Reports";
import UserActivityReport from "./pages/AdminDashboard/Reports/UserActivityReport";
import ReportsDashboard from "./pages/AdminDashboard/Reports/ReportsDashboard";
import { GlobalSnackbarProvider } from "./context/GlobalSnackbarContext";
import Users from "./pages/AdminDashboard/Users/Users";
import IndustryTrackContent from "./pages/AdminDashboard/IndustryTrackContent/IndustryTrackContent";
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;

const SetTriggerEffect = () => {
  const { triggerSessionExpiry } = useAuthContext();

  useEffect(() => {
    setExternalTrigger(triggerSessionExpiry);
  }, [triggerSessionExpiry]);

  return null;
};

const InactivityHandler = () => {
  const { triggerSessionExpiry, sessionExpired } = useAuthContext();
  const location = useLocation();

  useEffect(() => {
    const isAuthRoute =
      location.pathname === "/" ||
      location.pathname === constants.navigationUrls.login;

    if (sessionExpired || isAuthRoute) {
      return;
    }

    let timeoutId: number | undefined;
    const resetTimer = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        triggerSessionExpiry("inactivity");
      }, INACTIVITY_TIMEOUT_MS);
    };

    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [location.pathname, sessionExpired, triggerSessionExpiry]);

  return null;
};

const RootRedirect = () => {
  const token = constants.client_side_storage.accessToken;
  if (!token) return <Navigate to={constants.navigationUrls.login} replace />;
  const isAdmin = constants.client_side_storage.userDetails.is_admin;
  return (
    <Navigate
      to={isAdmin ? constants.navigationUrls.adminDashboard : constants.navigationUrls.userDashboard}
      replace
    />
  );
};

const App = () => {
  const [clientId, setClientId] = useState<any>("");

  useEffect(() => {
    const id = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    setClientId(id);
  }, []);
  return (
    <div>
      <GoogleOAuthProvider clientId={clientId}>
        <AuthProvider>
          <GlobalSnackbarProvider>
            <SetTriggerEffect />
            <InactivityHandler />
            <SessionExpiredModal />
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route
                path={constants.navigationUrls.login}
                element={<GoogleLoginComponent />}
              />

              <Route element={<ProtectedRoute allowedRoles={[1]} />}>
                <Route
                  element={<Navbar name={"Pre-Sales Admin Dashboard"} />}
                >
                  <Route
                    path={constants.navigationUrls.caseStudies}
                    element={<FileUpload defaultTab={0} />}
                  />
                  <Route
                    path={constants.navigationUrls.videos}
                    element={<FileUpload defaultTab={1} />}
                  />
                  <Route
                    path={constants.navigationUrls.users}
                    element={<Users />}
                  />
                  <Route
                    path={constants.navigationUrls.industryTrack}
                    element={<IndustryTrackUpload />}
                  />
                  <Route
                    path={constants.navigationUrls.certifications}
                    element={<CertificationsList />}
                  />
                  <Route
                    path={`${constants.navigationUrls.documentDetails}/:id`}
                    element={<ProjectDocumentDetails />}
                  />
                  <Route
                    path={`${constants.navigationUrls.industryTrackDetails}/:id`}
                    element={<IndustryTrackDetails />}
                  />
                  <Route
                    path={constants.navigationUrls.industryTrackContent}
                    element={<IndustryTrackContent />}
                  />
                  <Route path={constants.navigationUrls.reports} element={<ReportsDashboard/>}/>
                  <Route path={constants.navigationUrls.userActivityReport} element={<UserActivityReport/>}/>
                  <Route
                    path={`${constants.navigationUrls.videoDetails}/:id`}
                    element={<VideoDetails />}
                  />
                </Route>
              </Route>

              <Route element={<ProtectedRoute allowedRoles={[0, 1]} />}>
                <Route
                  path={constants.navigationUrls.userDashboard}
                  element={<UserDashboard />}
                />
                <Route path="/ppt-genrator" element={<PPT />} />
              </Route>

              <Route path="*" element={<Error404Page />} />
            </Routes>
          </GlobalSnackbarProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </div>
  );
};

export default App;
